import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

// SERVICES
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerService } from 'ngx-spinner';
import { ApiService } from 'src/app/services/api.service';
import { RequestsService } from 'src/app/core/services/http/requests.service';

// TYPES
import { ImageFile } from 'src/app/core/model/common/ImageFile.interface';

// CONSTANTS
import { Urls } from 'src/app/lib/urls';

export interface BillerFormData {
  billerName: string;
  billerDescription: string;
  firstFieldName: string;
  firstFieldWidth: number;
  secondFieldName: string;
  secondFieldWidth: number;
  convenienceFee: number;
  billerCategory: string;
  firstFieldFormat: string;
  secondFieldFormat: string;
  platform?: string;
  serviceFeePercentage: number;
  serviceFeeFixed: number;
  image?: File;
}

@Injectable({
  providedIn: 'root'
})
export class PayBillsService {
  
  // Image validation config
  private readonly MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
  private readonly MAX_IMAGE_DIMENSION = 1000; // Max 1000px for both width and height

  constructor(
    private apiService: ApiService,
    private requestsService: RequestsService,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService
  ) {}

  // =============================================
  // GETTER METHODS
  // =============================================

  get maxFileSize(): number {
    return this.MAX_FILE_SIZE;
  }

  get maxImageDimension(): number {
    return this.MAX_IMAGE_DIMENSION;
  }

  // =============================================
  // API METHODS
  // =============================================

  async performStore(formData: BillerFormData, imageFile?: ImageFile): Promise<any> {
    this.spinner.show().catch(err => console.error('Spinner show error:', err));

    try {
      const data = new FormData();
      const modifiedConvFee = formData.convenienceFee * 100;

      data.append('billerName', formData.billerName);
      data.append('billerDescription', formData.billerDescription);
      data.append('firstFieldName', formData.firstFieldName);
      data.append('firstFieldWidth', formData.firstFieldWidth.toString());
      data.append('secondFieldName', formData.secondFieldName);
      data.append('secondFieldWidth', formData.secondFieldWidth.toString());
      data.append('convenienceFee', modifiedConvFee.toString());
      data.append('serviceFeePercentage', formData.serviceFeePercentage.toString());
      data.append('serviceFeeFixed', formData.serviceFeeFixed.toString());
      data.append('billerCategory', formData.billerCategory);
      data.append('firstFieldFormat', formData.firstFieldFormat);
      data.append('secondFieldFormat', formData.secondFieldFormat);
      data.append('platform', formData.platform || '1');

      if (imageFile && imageFile.file) {
        data.append('image', imageFile.file, imageFile.name);
      }

      const response: any = await this.apiService.saveBiller(data);

      if (response?.status === 200) {
        this.toastr.success("New Biller has been successfully saved.");
        return { success: true, data: response };
      } else {
        this.toastr.error(response?.error?.message || 'Unknown error', 'Something went wrong!');
        return { success: false, error: response?.error };
      }
    } catch (error) {

      const apiError   = error?.error?.message   || 'Something went wrong!';

      this.toastr.error(apiError, 'Failed to create biller');
      return { success: false, error };
    } finally {
      this.spinner.hide().catch(err => console.error('Spinner hide error:', err));
    }
  }

  async performUpdate(uid: number, formData: BillerFormData, imageFile?: ImageFile): Promise<any> {
    this.spinner.show().catch(err => console.error('Spinner show error:', err));

    try {
      const data = new FormData();

      data.append('billerName', formData.billerName);
      data.append('billerDescription', formData.billerDescription);
      data.append('firstFieldName', formData.firstFieldName);
      data.append('firstFieldWidth', formData.firstFieldWidth.toString());
      data.append('secondFieldName', formData.secondFieldName);
      data.append('secondFieldWidth', formData.secondFieldWidth.toString());
      data.append('convenienceFee', formData.convenienceFee.toString());
      data.append('serviceFeePercentage', formData.serviceFeePercentage.toString());
      data.append('serviceFeeFixed', formData.serviceFeeFixed.toString());
      data.append('billerCategory', formData.billerCategory);
      data.append('firstFieldFormat', formData.firstFieldFormat);
      data.append('secondFieldFormat', formData.secondFieldFormat);

      if (imageFile && imageFile.file) {
        data.append('image', imageFile.file, imageFile.name);
      }

      const response = await this.apiService.updateBiller(uid, data);

      if (response.status === 200) {
        this.toastr.success("Biller update has been successfully saved.");
        return { success: true, data: response };
      } else {
        this.toastr.error(response.error?.message + '!', 'Something went wrong!');
        return { success: false, error: response.error };
      }
    } catch (error) {
      const apiError   = error?.error?.message   || 'Something went wrong!';

      this.toastr.error(apiError, 'Failed to update biller');
      return { success: false, error };
    } finally {
      this.spinner.hide().catch(err => console.error('Spinner hide error:', err));
    }
  }

  async performStatusChange(id: number, status: number): Promise<any> {
    try {
      const response: any = await firstValueFrom(
        this.apiService.updateBillerStatus(id, status)
      );

      this.toastr.success(response.message + '!', 'Success!');

      return { success: true, data: response };

    } catch (error: any) {

      this.toastr.error(
        error?.error?.message || 'Failed to update biller status',
        'Failed!'
      );

      return { success: false, error };
    }
  }

  async getBillersList(page: number, size: number, searchKeyword: string, sortColumn: string, sortType: 'ASC' | 'DESC'): Promise<any> {
    const params = {
      search: searchKeyword,
      sortBy: {
        [sortColumn]: sortType,
      },
    };

    try {

      const response = await firstValueFrom(
        this.apiService.getPaginatedBillers(params, page, size)
      );

      return response;
    } catch (error) {
      this.toastr.error('Failed to fetch billers list', 'Error');
      throw error;
    }
  }

  async getBillerByName(billerName: string): Promise<any> {
    const params = {
      billerName,
    };

    return await this.requestsService.post(Urls.billers + '?page=1&size=1', params);
  }

  async getFieldNames(): Promise<any> {
    return await this.requestsService.get(Urls.fields_billers);
  }

  getValidationError(control: any): string {
    if (!control || !control.errors || !control.touched) {
      return '';
    }

    if (control.errors['maxFileSize']) {
      const error = control.errors['maxFileSize'];
      return `File size exceeds ${(error.requiredSize / 1024 / 1024).toFixed(2)}MB`;
    }
    
    if (control.errors['invalidDimensions']) {
      const error = control.errors['invalidDimensions'];
      return `Image dimensions must not exceed ${error.maxDimension}x${error.maxDimension}px (actual: ${error.actualWidth}x${error.actualHeight}px)`;
    }
    
    if (control.errors['invalidImage']) {
      return 'Invalid image file';
    }
    
    if (control.errors['fileReadError']) {
      return 'Error reading file';
    }
    
    return '';
  }

  // =============================================
  // CATEGORY UTILITIES
  // =============================================

  getCategoryText(categoryId: number): string {
    const categories = {
      2: 'Telecom',
      3: 'Credit Card',
      4: 'Cable and Internet',
      5: 'Government',
      6: 'Loans',
      7: 'Others',
      8: 'Travel',
      9: 'Transpo',
      10: 'Shopping',
      11: 'Power',
      12: 'Water'
    };
    return categories[categoryId] || '';
  }

  getFormatText(formatId: number): string {
    const formats = {
      1: 'Numeric',
      2: 'Alphanumeric'
    };
    return formats[formatId] || '';
  }
}
